import React from 'react';

import type {
  TableHeaderProps,
  RowHeaderProps,
  AttributeRowProps,
  TableBodyProps,
  CompareTableProps,
} from './types';
import { useBreakpoint } from '../breakpoint';
import Element from '../../../core/Element';

const DEFAULT_STYLES_OBJECT = {};

const TableHeader = ({
  cards,
  styles,
  qeId,
}: TableHeaderProps): React.ReactElement => {
  const { sm } = useBreakpoint();

  return (
    <thead className={styles.thead}>
      <tr>
        <>
          {!sm && <td />}
          {cards.map((card, i) => (
            <th id={`${qeId}-col-head-${i + 1}`} scope="col" key={i}>
              {card ?? 'Not Available'}
            </th>
          ))}
        </>
      </tr>
    </thead>
  );
};

const RowHeader = ({
  attributeName,
  Tooltip,
}: RowHeaderProps): React.ReactElement => {
  return (
    <>
      <Element>{attributeName}</Element>
      {Tooltip}
    </>
  );
};

const AttributeRow = ({
  attributeKey,
  attributeContent = {},
  cardContent = [],
  showOnlyDifferentAttributes,
  styles,
  qeId,
  rowIndex,
}: AttributeRowProps): React.ReactElement | null => {
  const { sm } = useBreakpoint();
  const { header, Tooltip } = attributeContent[attributeKey] || {};
  const rowHeaderId = `${qeId}-row-head-${rowIndex}`;
  const allAttributesAreTheSame = cardContent.every(
    (card) =>
      card?.[attributeKey]?.key === cardContent?.[0]?.[attributeKey]?.key ||
      card === null
  );

  if (showOnlyDifferentAttributes && allAttributesAreTheSame) {
    return null;
  }

  if (sm) {
    return (
      <>
        <tr className={styles.mobileTrHeader}>
          <th className={styles.th} scope="row" id={rowHeaderId} colSpan={2}>
            <RowHeader attributeName={header} Tooltip={Tooltip} />
          </th>
        </tr>
        <tr className={styles.mobileTr}>
          {cardContent.map((content, i) => (
            <td
              key={`${content?.[attributeKey]?.key}_${i}`}
              className={styles.td}
              headers={`${qeId}-col-head-${i + 1} ${rowHeaderId}`}
            >
              {content?.[attributeKey]?.Component}
            </td>
          ))}
        </tr>
      </>
    );
  } else {
    return (
      <tr className={styles.tr}>
        <th className={styles.th} scope="row" id={rowHeaderId}>
          <RowHeader attributeName={header} Tooltip={Tooltip} />
        </th>
        {cardContent.map((content, i) => (
          <td
            key={`${content?.[attributeKey]?.key}_${i}`}
            className={styles.td}
            headers={`${qeId}-col-head-${i + 1} ${rowHeaderId}`}
          >
            {content?.[attributeKey]?.Component}
          </td>
        ))}
      </tr>
    );
  }
};

const TableBody = ({
  cardAttributes,
  showOnlyDifferentAttributes,
  attributeContent,
  cardContent,
  styles,
  qeId,
}: TableBodyProps): React.ReactElement | null => {
  if (!Array.isArray(cardAttributes) || cardAttributes.length === 0)
    return null;

  return (
    <tbody className={styles.tbody}>
      {cardAttributes.map((attributeKey, i) => (
        <AttributeRow
          key={attributeKey}
          attributeKey={attributeKey}
          attributeContent={attributeContent}
          showOnlyDifferentAttributes={showOnlyDifferentAttributes}
          cardContent={cardContent}
          styles={styles}
          qeId={qeId}
          rowIndex={i}
        />
      ))}
    </tbody>
  );
};

export const CompareTable = ({
  cards,
  cardAttributes,
  showOnlyDifferentAttributes,
  attributeContent,
  cardContent,
  tableName,
  styles = DEFAULT_STYLES_OBJECT,
  qeId = 'compareTable',
}: CompareTableProps): React.ReactElement => {
  return (
    <table data-qe-id={qeId} className={styles.table}>
      <caption className={styles.caption}>{tableName}</caption>
      <TableHeader cards={cards} styles={styles} qeId={qeId} />
      <TableBody
        cardAttributes={cardAttributes}
        showOnlyDifferentAttributes={showOnlyDifferentAttributes}
        attributeContent={attributeContent}
        cardContent={cardContent}
        styles={styles}
        qeId={qeId}
      />
    </table>
  );
};
